import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, ImageDown, Copy, Check, Loader2, Megaphone, Printer } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";

const SITE_URL = "https://neox.lk";

const PRESETS = [
  {
    id: "free",
    bg: "linear-gradient(135deg, #1C140E 0%, #3B2A16 55%, #92400E 150%)",
    accent: "#F5C97A",
    head_si: "පැරණි සිංහල ගී — නොමිලේ",
    sub_en: "Old Sinhala songs · Free · No login",
    caption:
      "🪔 ස්වාරාන්ජලිය — පැරණි සිංහල ගී, කතන්දර සහ භාවනා. නොමිලේ, ලොග් වීමක් නැහැ. මනස නැවුම් කරන සන්සුන් කාමරයක්.\n👉 neox.lk\n\n🪔 Swaranjaliya — calm old Sinhala songs, stories & meditation. Free, no login. A peaceful room for the heart.\n👉 neox.lk\n#Swaranjaliya #Neox #SriLanka",
  },
  {
    id: "parents",
    bg: "linear-gradient(135deg, #14210E 0%, #26361A 55%, #3F6212 150%)",
    accent: "#BEF264",
    head_si: "අම්මට · තාත්තට තෑග්ගක්",
    sub_en: "A gift for your parents · anywhere in the world",
    caption:
      "🪔 විදේශගතව සිටින දරුවනේ — අම්මට තාත්තට හොඳම තෑග්ග මෙන්න. පැරණි සිංහල ගී, පිරිත්, කතන්දර — නොමිලේ.\n👉 neox.lk\n\n🪔 For children abroad — the loveliest gift for your parents: old Sinhala songs, pirith & stories. Free.\n👉 neox.lk\n#Neox #Swaranjaliya #SriLankaDiaspora",
  },
  {
    id: "karaoke",
    bg: "linear-gradient(135deg, #1A1024 0%, #2E1A3B 55%, #7E22CE 150%)",
    accent: "#E9D5FF",
    head_si: "කැරෝකේ — එකට ගයමු",
    sub_en: "New Karaoke Room · sing your favourites",
    caption:
      "🎤 ස්වාරාන්ජලිය අලුත් කැරෝකේ කාමරේ! ඔබේ ප්‍රියතම පැරණි සිංදු එකට ගයන්න. පවුලේ හැමෝටම.\n👉 neox.lk\n\n🎤 New Karaoke Room at Swaranjaliya — sing your favourite old songs together, for the whole family.\n👉 neox.lk\n#Neox #Karaoke #Swaranjaliya",
  },
  {
    id: "poya",
    bg: "linear-gradient(135deg, #0E1A24 0%, #16303B 55%, #0E7490 150%)",
    accent: "#A5F3FC",
    head_si: "පොහොය දිනට · පිරිත් හා ගාථා",
    sub_en: "For Poya day · Pirith, Gatha & calm chants",
    caption:
      "🌕 සුබ පොහොය දිනයක්! ස්වාරාන්ජලිය පිරිත්, ගාථා සහ සන්සුන් ගී — මනසට සැනසුමක්. නොමිලේ.\n👉 neox.lk\n\n🌕 A blessed Poya day. Pirith, Gatha & calm chants at Swaranjaliya — peace for the mind. Free.\n👉 neox.lk\n#Poya #Neox #Swaranjaliya",
  },
  {
    id: "poets",
    bg: "linear-gradient(135deg, #241004 0%, #3B1F0E 55%, #B45309 150%)",
    accent: "#FDBA74",
    head_si: "කවි කොන · ඔබේ කවිය ලියන්න",
    sub_en: "Poets' Corner · write & share your poem",
    caption:
      "✍️ ස්වාරාන්ජලිය කවි කොනට ඔබේ සිංහල කවිය එවන්න — ජංගම දුරකථනයෙන්ම type කරන්න පුළුවන්. නොමිලේ.\n👉 neox.lk\n\n✍️ Share your Sinhala poem at the Poets' Corner — type it right on your phone. Free.\n👉 neox.lk\n#PoetsCorner #Neox #SinhalaPoetry",
  },
  {
    id: "newsong",
    bg: "linear-gradient(135deg, #10141C 0%, #1E293B 55%, #4338CA 150%)",
    accent: "#C7D2FE",
    head_si: "අද දිනයේ සින්දුව",
    sub_en: "Song of the day · a new gift every morning",
    caption:
      "🎶 හැම උදෑසනකම අලුත් 'අද දිනයේ සින්දුව' ස්වාරාන්ජලිය. අද මොකක්ද කියලා බලන්න!\n👉 neox.lk\n\n🎶 A fresh 'Song of the day' every morning at Swaranjaliya. Come see today's pick!\n👉 neox.lk\n#Neox #Swaranjaliya #SongOfTheDay",
  },
];

const KitArt = ({ preset, fontClass, innerRef }) => (
  <div
    ref={innerRef}
    style={{
      width: 1080, height: 1080, position: "relative", background: preset.bg, color: "#fff",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "90px 80px", boxSizing: "border-box", textAlign: "center",
    }}
  >
    <div style={{ fontSize: 120, lineHeight: 1 }}>🪔</div>
    <div className="font-en" style={{ fontSize: 30, letterSpacing: 8, color: preset.accent, marginTop: 26, fontWeight: 800, textTransform: "uppercase" }}>
      NEOX · ස්වාරාන්ජලිය
    </div>
    <div className={fontClass()} style={{ fontSize: 92, fontWeight: 800, marginTop: 28, lineHeight: 1.25 }}>
      {preset.head_si}
    </div>
    <div className="font-en" style={{ fontSize: 40, color: "rgba(255,255,255,0.82)", marginTop: 22, fontWeight: 700 }}>
      {preset.sub_en}
    </div>
    <div style={{ width: 140, height: 6, background: preset.accent, borderRadius: 6, margin: "48px 0" }} />
    <div style={{ display: "flex", alignItems: "center", gap: 34 }}>
      <div style={{ background: "#fff", borderRadius: 22, padding: 20 }}>
        <QRCodeCanvas value={SITE_URL} size={150} bgColor="#ffffff" fgColor="#1C140E" level="M" includeMargin={false} />
      </div>
      <div style={{ textAlign: "left" }}>
        <div className="font-en" style={{ fontSize: 32, color: "rgba(255,255,255,0.8)", fontWeight: 700 }}>Free · No login</div>
        <div className="font-en" style={{ fontSize: 64, fontWeight: 800, letterSpacing: 2, marginTop: 6 }}>neox.lk</div>
      </div>
    </div>
    <div className="font-en" style={{ position: "absolute", bottom: 46, fontSize: 24, color: "rgba(255,255,255,0.5)", fontWeight: 600, letterSpacing: 1 }}>
      A FutureTec Production
    </div>
  </div>
);

const KitCard = ({ preset, fontClass }) => {
  const artRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const download = async () => {
    if (!artRef.current) return;
    setBusy(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(artRef.current, { scale: 1, useCORS: true });
      const link = document.createElement("a");
      link.download = `neox-post-${preset.id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image saved — ready to post on Facebook 🎉");
    } catch (e) {
      toast.error("Could not save the image on this device.");
    } finally { setBusy(false); }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(preset.caption);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success("Caption copied");
    } catch (e) { toast.error("Could not copy. Please select and copy manually."); }
  };

  const printPoster = async () => {
    if (!artRef.current) return;
    setBusy(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(artRef.current, { scale: 1, useCORS: true });
      const url = canvas.toDataURL("image/png");
      const w = window.open("", "_blank");
      if (!w) { toast.error("Please allow pop-ups to print."); return; }
      w.document.write(`<html><head><title>Neox Poster</title><style>@page{size:A4;margin:12mm}html,body{margin:0;height:100%}body{display:flex;align-items:center;justify-content:center}img{max-width:100%;max-height:100%;object-fit:contain}</style></head><body><img src="${url}" onload="setTimeout(function(){window.print()},200)"/></body></html>`);
      w.document.close();
    } catch (e) { toast.error("Could not prepare the poster on this device."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl p-4" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid={`kit-card-${preset.id}`}>
      {/* Preview (scaled) */}
      <div className="mx-auto rounded-2xl overflow-hidden" style={{ width: 300, height: 300, maxWidth: "100%" }}>
        <div style={{ width: 1080, height: 1080, transform: "scale(0.2778)", transformOrigin: "top left" }}>
          <KitArt preset={preset} fontClass={fontClass} />
        </div>
      </div>
      <p className="font-ui text-xs mt-3 whitespace-pre-line line-clamp-4" style={{ color: "var(--text-muted)" }}>{preset.caption}</p>
      <div className="flex gap-2 mt-3">
        <button onClick={download} disabled={busy} data-testid={`kit-download-${preset.id}`}
          className="flex-1 h-12 rounded-xl font-ui font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ImageDown size={16} />} Download image
        </button>
        <button onClick={printPoster} disabled={busy} data-testid={`kit-print-${preset.id}`} aria-label="Print poster"
          className="h-12 px-4 rounded-xl font-ui font-bold text-sm flex items-center gap-2 disabled:opacity-60" style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
          <Printer size={16} /> Print
        </button>
        <button onClick={copyCaption} data-testid={`kit-copy-${preset.id}`}
          className="h-12 px-4 rounded-xl font-ui font-bold text-sm flex items-center gap-2" style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>
          {copied ? <Check size={16} /> : <Copy size={16} />} Caption
        </button>
      </div>

      {/* Hidden full-size for export */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <KitArt preset={preset} fontClass={fontClass} innerRef={artRef} />
      </div>
    </div>
  );
};

export default function PostKit() {
  const { fontClass } = useLang();
  return (
    <div className="min-h-screen pb-28" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/rooms/sindu-kamare/admin" data-testid="kit-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> Back to Admin
        </Link>
        <div className="text-center mb-8">
          <span className="grid place-items-center w-16 h-16 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Megaphone size={28} /></span>
          <h1 className={`${fontClass()} text-3xl font-bold mt-3`} style={{ color: "var(--text)" }}>Facebook Post Kit</h1>
          <p className="font-ui text-sm mt-1 max-w-lg mx-auto" style={{ color: "var(--text-muted)" }}>
            Ready-made square images + Sinhala/English captions. Download an image, copy its caption, and post to Facebook — free marketing for Neox.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="post-kit-grid">
          {PRESETS.map((p) => <KitCard key={p.id} preset={p} fontClass={fontClass} />)}
        </div>
      </div>
    </div>
  );
}
